import type { DomainModule, PageRegistration } from '../../contracts';
import type { Kind, Role } from '../../types';
import { scopeAllows } from './access';
import { knowledgeCommand, knowledgeQuery } from './knowledge';
import { learningCommand, learningQuery } from './learning';

export * from './access';
export * from './knowledge';
export * from './learning';
export * from './types';

const learningPages: PageRegistration[] = [
  ['T01', '/teaching/overview'],
  ['T02', '/teaching/lesson'],
  ['T03', '/teaching/lesson/:lessonUuid'],
  ['T04', '/teacher/:memberUuid'],
  ['T05', '/teaching/teacher'],
  ['T06', '/teaching/plan'],
].map(([id, path]) => ({
  id,
  path,
  resource:
    id === 'T04' || id === 'T05'
      ? 'teacher'
      : id === 'T06'
        ? 'plan'
        : 'teaching',
  parentPath: '/teaching/overview',
  returnTo: id === 'T04' ? '/teaching/teacher' : '/teaching/lesson',
}));
const knowledgePages: PageRegistration[] = [
  ['K01', '/knowledge'],
  ['K02', '/knowledge/:knowledgeUuid'],
  ['K03', '/knowledge/:knowledgeUuid/edit'],
  ['K04', '/knowledge/review'],
].map(([id, path]) => ({
  id,
  path,
  resource: 'knowledge',
  parentPath: '/knowledge',
  returnTo: '/knowledge',
}));
const roles: Role[] = [
  'teacher',
  'campusManager',
  'groupOperator',
  'regionManager',
  'researchLead',
];
function commands(
  names: string[],
  kind: Kind | undefined,
  execute: typeof learningCommand,
) {
  return Object.fromEntries(
    names.map((name) => [name, { roles, kind, execute }]),
  );
}
export const learningDomain: DomainModule = {
  namespace: 'learning',
  version: 1,
  resource: 'teaching',
  pages: learningPages,
  migrations: [
    {
      version: 1,
      up(state) {
        state.domainData ||= {};
        state.domainData.learning ||= {};
      },
    },
  ],
  metrics: (ctx) => {
    const view = learningQuery(ctx);
    return [
      {
        uuid: 'learning-confirmed-v1',
        scope: ctx.context.campus,
        asOf: ctx.now,
        metric: '已确认教学观察',
        numerator: view.metrics.confirmedObservations,
        denominator: null,
        unit: 'observation',
        sourceUuids: [
          ...new Set(
            view.observations
              .filter((o) => o.status === 'confirmed')
              .map((o) => o.lessonUuid),
          ),
        ],
        definitionVersion: 'T-C1-v1',
      },
      {
        uuid: 'learning-practice-v1',
        scope: ctx.context.campus,
        asOf: ctx.now,
        metric: '导师验收实践',
        numerator: view.metrics.completedPlans,
        denominator: view.metrics.plans || null,
        unit: 'plan',
        sourceUuids: view.plans
          .filter((p) => p.fields?.learningManaged === 'true')
          .map((p) => p.uuid),
        definitionVersion: 'T-C1-v1',
      },
    ];
  },
  commands: {
    ...commands(
      ['saveLesson', 'saveSection', 'observe', 'confirmObservation'],
      'lesson',
      learningCommand,
    ),
    ...commands(['createPlan'], 'plan', learningCommand),
    ...commands(['linkRecording'], 'recording', learningCommand),
    ...commands(
      ['assignMentor', 'saveProfile', 'training'],
      'teacher',
      learningCommand,
    ),
    ...commands(
      [
        'acceptPlan',
        'schedulePractice',
        'submitPractice',
        'reviewPractice',
        'reopenPlan',
        'cancelPlan',
      ],
      'plan',
      learningCommand,
    ),
  },
  query: learningQuery,
};
export const knowledgeDomain: DomainModule = {
  namespace: 'knowledge',
  version: 1,
  resource: 'knowledge',
  pages: knowledgePages,
  migrations: [
    {
      version: 1,
      up(state) {
        state.domainData ||= {};
        state.domainData.knowledge ||= {
          drafts: {},
          versions: {},
          reuse: [],
          favorites: [],
          views: [],
        };
      },
    },
  ],
  commands: commands(
    [
      'createDraft',
      'saveDraft',
      'submit',
      'review',
      'newVersion',
      'withdraw',
      'reuse',
      'view',
      'favorite',
      'feedback',
    ],
    'knowledge',
    knowledgeCommand,
  ),
  query: knowledgeQuery,
  search: (ctx) =>
    knowledgeQuery(ctx).flatMap((v) =>
      v.published &&
      scopeAllows(v.published.scope, ctx.context) &&
      v.entity.status === 'published' &&
      !v.sourceChanged
        ? [
            {
              id: `knowledge:${v.published.uuid}`,
              entityUuid: v.entity.uuid,
              title: v.published.title,
              text: [
                v.published.topic,
                v.published.situation,
                v.published.steps,
                v.published.conditions,
                v.published.limitations,
                v.published.body,
              ].join('\n'),
              kind: 'knowledge' as const,
              memberUuid: v.entity.memberUuid,
              occurredAt: v.published.reviewedAt || v.entity.updatedAt,
            },
          ]
        : [],
    ),
};

for (const name of ['view', 'favorite', 'feedback'])
  knowledgeDomain.commands[name].roles = [...roles, 'groupLeader'];
